/* eslint-disable react-refresh/only-export-components -- context provider and
   its hook live together by design (fixture notification inbox, design B5). */
import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuth } from '@quickbee/auth';
import {
  useListMyKycNoticesQuery,
  useMarkKycNoticeReadMutation,
  type KycNoticeResponse
} from '@quickbee/api-identity';
import { notifications as initialNotifications, type NotificationItem } from '@/mocks/notificationsData';

interface NotificationContextValue {
  items: NotificationItem[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllRead: () => void;
  refresh: () => Promise<void>;
  setItems: (updater: NotificationItem[] | ((prev: NotificationItem[]) => NotificationItem[])) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

function formatNoticeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfThatDay.getTime()) / 86_400_000);
  if (dayDiff === 0) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (dayDiff === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function toKycNotification(notice: KycNoticeResponse, locallyRead: ReadonlySet<string>): NotificationItem {
  return {
    id: notice.id,
    type: 'kyc',
    title: notice.title,
    description: notice.body,
    time: formatNoticeTime(notice.createdAt),
    timestamp: new Date(notice.createdAt),
    read: Boolean(notice.readAt) || locallyRead.has(notice.id),
    actionable: true,
    actionLabel: 'View KYC',
    deepLink: '/account/profile'
  };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const noticesQuery = useListMyKycNoticesQuery(isAuthenticated ? undefined : skipToken);
  const [markNoticeRead] = useMarkKycNoticeReadMutation();
  const [items, setItems] = useState<NotificationItem[]>(initialNotifications);
  const [locallyRead, setLocallyRead] = useState<Set<string>>(() => new Set());

  const kycItems = useMemo(
    () => (noticesQuery.data ?? []).map((notice) => toKycNotification(notice, locallyRead)),
    [noticesQuery.data, locallyRead]
  );
  const kycIds = useMemo(() => new Set(kycItems.map((item) => item.id)), [kycItems]);

  const merged = useMemo(() => {
    const combined = [...kycItems, ...items];
    combined.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return combined;
  }, [items, kycItems]);

  const unreadCount = useMemo(() => merged.filter((n) => !n.read).length, [merged]);

  const markAsRead = useCallback((id: string) => {
    if (kycIds.has(id)) {
      setLocallyRead((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      void markNoticeRead({ id });
      return;
    }
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, [kycIds, markNoticeRead]);

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    const unreadLive = kycItems.filter((n) => !n.read);
    if (unreadLive.length === 0) return;
    setLocallyRead((prev) => {
      const next = new Set(prev);
      for (const notice of unreadLive) next.add(notice.id);
      return next;
    });
    for (const notice of unreadLive) {
      void markNoticeRead({ id: notice.id });
    }
  }, [kycItems, markNoticeRead]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    await noticesQuery.refetch();
  }, [isAuthenticated, noticesQuery]);

  return (
    <NotificationContext.Provider value={{ items: merged, unreadCount, markAsRead, markAllRead, refresh, setItems }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
